import ShapeConvert from '@src/slide/domain/ui-model/element-modeles/convert/shapeConvert';
import {
    convertBoolToNumber,
    convertLayoutSdkToUi,
    convertLayoutUiToSdk,
} from '@src/slide/domain/ui-model/element-modeles/convert/chartConvert';
import _has from 'lodash/has';
import { setUIValueToSdkData } from '@src/slide/domain/ui-model/element-modeles/convert/sub-chart-convert/utils';
import i18n from '@i18next/i18n';
import { LabelPosition } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v2/subchart/common/data-label.interface';
import type {
    ChartManualLayout,
    ShapeProps,
    TextProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v2/style.interface';
import { PresetChartType } from '../../../chart-presets/const';

// 下面的"可选属性"都加了declare的原因是：
// 最近的master更新了Babel配置：allowDeclareFields: true，会导致class的可选属性被初始化为undefined。
// 在DataLabel的基类这些都是不希望被初始化（会有解构逻辑），所以增加declare声明
export class BaseDataLabel implements ShapeProps, TextProps {
    // 数据标签里是否展示图例名称
    declare public showLegendKey?: boolean;
    // 是否展示数值
    public showVal: boolean;
    // 是否展示类别名称
    declare public showCategoryName?: boolean;
    // 是否展示系列名称
    declare public showSeriesName?: boolean;
    // 是否展示系列百分比（目前发现饼图中可以设置）
    declare public showPercent?: boolean;
    // 是否展示气泡大小
    declare public showBubbleSize?: boolean;
    // 标签位置，支持类型为LabelPositionType
    declare public labelPosition?: LabelPosition;
    // 标签内容的分割符，在配置展示图例形状、类别名称等中分隔。默认逗号
    declare public separator?: string;
    declare public props?: any;
    declare public paragraphList?: Array<any>;
    declare public textBodyStyle?: any;
    // 自定义标签内容
    declare public text?: any;
    // label自定义位置
    declare public layout?: ChartManualLayout;
    declare public numFmt?: any;
    declare public formatCode?: string;
    // 是否被删除了
    declare public delete: boolean;
    public seriesId?: string;

    public constructor(dataLabel, seriesId?) {
        const {
            showLegendKey, showVal, showCategoryName,
            showSeriesName, showPercent, labelPosition,
            showBubbleSize, props, separator, text,
            numFmt,
        } = dataLabel;

        this.seriesId = seriesId;
        this.showLegendKey = showLegendKey;
        this.showVal = showVal;
        this.showCategoryName = showCategoryName;
        this.showSeriesName = showSeriesName;
        this.showPercent = showPercent;
        this.labelPosition = labelPosition;
        this.showBubbleSize = showBubbleSize;
        this.separator = separator;
        this.numFmt = numFmt;

        if (_has(dataLabel, 'delete')) {
            this.delete = !!dataLabel.delete;
        }

        if (props) {
            ShapeConvert.convertDataSdkToUi(dataLabel, this, undefined);
        }
        // 自定义标签文本
        if (text) {
            this.text = {};
            ShapeConvert.convertDataSdkToUi(text, this.text, undefined);
        }

        if (numFmt) {
            this.formatCode = numFmt.formatCode;
        }

        convertLayoutSdkToUi(dataLabel, this);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering, complexity
    public static convertDataUiToSdk(uiData) {
        const sdkData: any = {};
        const { showLegendKey, showVal, showCategoryName, showSeriesName, showPercent, labelPosition,
            showBubbleSize, separator, numFmt, text, formatCode } = uiData || { };

        if (_has(uiData, 'showLegendKey')) {
            setUIValueToSdkData(sdkData, 'showLegendKey', convertBoolToNumber(showLegendKey));
        }
        if (_has(uiData, 'showVal')) {
            setUIValueToSdkData(sdkData, 'showVal', convertBoolToNumber(showVal));
        }
        if (_has(uiData, 'showCategoryName')) {
            setUIValueToSdkData(sdkData, 'showCategoryName', convertBoolToNumber(showCategoryName));
        }
        if (_has(uiData, 'showSeriesName')) {
            setUIValueToSdkData(sdkData, 'showSeriesName', convertBoolToNumber(showSeriesName));
        }
        if (_has(uiData, 'showPercent')) {
            setUIValueToSdkData(sdkData, 'showPercent', convertBoolToNumber(showPercent));
        }
        if (_has(uiData, 'labelPosition')) {
            setUIValueToSdkData(sdkData, 'labelPosition', labelPosition);
        }
        if (_has(uiData, 'showBubbleSize')) {
            setUIValueToSdkData(sdkData, 'showBubbleSize', convertBoolToNumber(showBubbleSize));
        }
        if (_has(uiData, 'separator')) {
            setUIValueToSdkData(sdkData, 'separator', separator);
        }
        if (_has(uiData, 'numFmt')) {
            setUIValueToSdkData(sdkData, 'numFmt', numFmt);
        }
        if (_has(uiData, 'delete')) {
            setUIValueToSdkData(sdkData, 'delete', convertBoolToNumber(uiData.delete));
        }

        if (_has(uiData, 'text')) {
            sdkData.text = {};
            ShapeConvert.convertDataUiToSdk(text, sdkData.text, {});
        }
        ShapeConvert.convertDataUiToSdk(uiData, sdkData, {});
        convertLayoutUiToSdk(uiData, sdkData);

        return sdkData;
    }
}

export class DataLabel extends BaseDataLabel {
    // 标识自己是ser中哪一个datalist下
    public dataId?: string;

    public constructor(props, seriesId: string, dataId?: string) {
        super(props, seriesId);
        this.dataId = dataId;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static convertDataUiToSdk(uiData) {
        if (!uiData) return uiData; // 空数据字段直接返回
        const sdkData = super.convertDataUiToSdk(uiData);

        if (_has(uiData, 'id')) {
            sdkData.id = uiData.id;
        }

        return sdkData;
    }
}

export class DataLabels extends BaseDataLabel {
    public showLeaderLines: boolean;

    public constructor(props, seriesId?) {
        super(props, seriesId);

        const { showLeaderLines } = props;
        this.showLeaderLines = !!showLeaderLines;

        ShapeConvert.convertDataSdkToUi(props, this, undefined);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static convertDataUiToSdk(uiData) {
        const sdkData = super.convertDataUiToSdk(uiData);

        const { dataLabelList, showLeaderLines } = uiData;

        setUIValueToSdkData(sdkData, 'showLeaderLines', convertBoolToNumber(showLeaderLines));
        if (dataLabelList) {
            sdkData.dataLabel = dataLabelList.map(dataLabel => dataLabel.convertDataUiToSdk());
        }

        return sdkData;
    }
}
